import React, { useEffect, useState } from 'react';
import { animated } from 'react-spring';

import { IS_ON_PREMISE } from '@/constants/configConstants';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';
import { Button } from '@design-system-outdated/components';
import { FullWidthBasicModal } from '@DesignSystem/modals';
import { useSendPostSignUpMutation } from '@onboarding/api';
import { useUpdateUserSettingsMutation } from '@shared/api';
import { APPLICATION_TYPE, LLM_APPLICATION_URL } from '@shared/constants';
import { mountTapfiliate } from '@shared/utils/useTapfiliate';
import { ApplicationCard } from './ApplicationCard';
import styles from './PostSignUpQuestions.module.scss';
import usePostSignUpSprings from './usePostSignUpSprings';
import {
  useActiveWorkspaceName,
  useBetaFeatureEnabled,
  useIsSelectedAppLLM
} from '@shared/hooks';
import { BETA_FEATURE_LLM_EVAL } from '@/lib/betaFeatures';

const ONBOARDING_USER_TEAM_SIZE = ['Under 5', '5-10', '11-50', 'Over 50'];
const ONBOARDING_ROLES = [
  'Data scientist',
  'Data engineer',
  'Software engineer',
  'VP/Dir/Mgr of data science team',
  'Founder',
  'Academic',
  'Other'
];
const ONBOARDING_KIND_OF_MODELING = [
  'Computer vision',
  'Natural language processing',
  'Reinforcement learning',
  'Robotics',
  'Generative AI',
  'Speech recognition',
  'Structured data',
  'Other'
];
const ONBOARDING_KIND_OF_MODELING_LLM = [
  'Virtual assistant',
  'Content generation',
  'Development tools',
  'Education platform',
  'Translation',
  'Other'
];

const PostSignUpQuestions = () => {
  const isLLMEvalEnabled = useBetaFeatureEnabled(BETA_FEATURE_LLM_EVAL);
  const updateUserSettingsMutation = useUpdateUserSettingsMutation();
  const sendPostSignUpFormMutation = useSendPostSignUpMutation();
  const activeWorkspaceName = useActiveWorkspaceName();
  const { user: currentUser } = useCurrentUserContext();
  const isUserLoggedIn = currentUser?.loggedIn;
  const isPostSignupFormAlreadyShown = currentUser?.showPostSignupForm;
  const isSelectedAppLLM = useIsSelectedAppLLM();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [applicationType, setApplicationType] = useState<APPLICATION_TYPE>(
    isSelectedAppLLM ? APPLICATION_TYPE.llm : APPLICATION_TYPE.em
  );
  const [teamSize, setTeamSize] = useState('');
  const [role, setRole] = useState('');
  const [modeling, setModeling] = useState('');

  const [isRoleInitialized, setIsRoleInitialized] = useState(false);
  const [isTeamSizeInitialized, setIsTeamSizeInitialized] = useState(false);

  const { springsRole, springsModeling } = usePostSignUpSprings({
    isTeamSizeInitialized,
    isRoleInitialized
  });

  const renderPickApplication = (
    onPickApplication: (appType: APPLICATION_TYPE) => void
  ) => {
    return (
      <div className={styles.appPickerContainer}>
        <div
          className={styles.appOptionContainer}
          onClick={() => onPickApplication(APPLICATION_TYPE.llm)}
        >
          <ApplicationCard
            description="I’m building RAG pipelines, Agents or performing prompt engineering tasks"
            headerColor="#19A979"
            imageSrc="/images/onboarding/app-llm.png"
            title="Building with LLMs (Opik)"
          />
        </div>

        <div
          className={styles.appOptionContainer}
          onClick={() => onPickApplication(APPLICATION_TYPE.em)}
        >
          <ApplicationCard
            description="I’m training or fine tuning ML models using Pytorch, Scikit-learn or similar frameworks"
            headerColor="#6C6FF7"
            imageSrc="/images/onboarding/app-em.png"
            title="Training and fine-tuning models"
          />
        </div>
      </div>
    );
  };

  const renderTellUsMoreContent = () => {
    return (
      <div className={styles.postSignUpForm}>
        <div className={styles.postSignUpField}>
          <div className={styles.question}>
            How many people are in your team?
            <span className={styles.asterrisk}> *</span>
          </div>

          <div className={styles.fieldOptions}>
            {ONBOARDING_USER_TEAM_SIZE.map(size => (
              <div key={size} className={styles.fieldOption}>
                <Button
                  type="outlinedRounded"
                  onClick={() => {
                    setTeamSize(size);
                    setIsTeamSizeInitialized(true);
                  }}
                  active={size === teamSize}
                >
                  {size}
                </Button>
              </div>
            ))}
          </div>
        </div>
        {teamSize ? (
          <animated.div className={styles.postSignUpField} style={springsRole}>
            <div className={styles.question}>
              What best describes your current role?
              <span className={styles.asterrisk}> *</span>
            </div>

            <div className={styles.fieldOptions}>
              {ONBOARDING_ROLES.map(currentRole => (
                <div key={currentRole} className={styles.fieldOption}>
                  <Button
                    type="outlinedRounded"
                    onClick={() => {
                      setRole(currentRole);
                      setIsRoleInitialized(true);
                    }}
                    active={role === currentRole}
                  >
                    {currentRole}
                  </Button>
                </div>
              ))}
            </div>
          </animated.div>
        ) : null}
        {role ? (
          <animated.div
            className={styles.postSignUpField}
            style={springsModeling}
          >
            <div className={styles.postSignUpField}>
              <div className={styles.question}>
                What kind of modeling are you working on?
                <span className={styles.asterrisk}> *</span>
              </div>

              <div className={styles.fieldOptions}>
                {(applicationType === APPLICATION_TYPE.em
                  ? ONBOARDING_KIND_OF_MODELING
                  : ONBOARDING_KIND_OF_MODELING_LLM
                ).map(currentModeling => (
                  <div key={currentModeling} className={styles.fieldOption}>
                    <Button
                      type="outlinedRounded"
                      onClick={() => setModeling(currentModeling)}
                      active={modeling === currentModeling}
                    >
                      {currentModeling}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </animated.div>
        ) : null}
      </div>
    );
  };

  const submitFormAnswersButton = async (userAppType: APPLICATION_TYPE) => {
    try {
      await updateUserSettingsMutation.mutateAsync({
        updates: { userAppType }
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      await sendPostSignUpFormMutation.mutateAsync({
        role,
        sizeOfTeam: teamSize,
        modellingArea: modeling
      });
    } finally {
      if (userAppType === APPLICATION_TYPE.llm) {
        window.location.href = `${LLM_APPLICATION_URL}${activeWorkspaceName}/get-started`;
      }
    }
  };

  // @todo: why is this here?
  useEffect(() => {
    mountTapfiliate();
  }, []);

  const selectApplicationType = (appType: APPLICATION_TYPE) => {
    const shouldSkipQuestionList = appType === APPLICATION_TYPE.llm;

    if (shouldSkipQuestionList) {
      submitFormAnswersButton(APPLICATION_TYPE.llm);

      return;
    }

    setApplicationType(appType);
    setActiveStepIndex(1);
  };

  const shouldDisplayPickApp = isLLMEvalEnabled && !isSelectedAppLLM;

  const shouldDisplayPickAppOnPrem =
    IS_ON_PREMISE &&
    window.location.pathname === '/get-started' &&
    shouldDisplayPickApp;

  const getTab = () => {
    if (shouldDisplayPickAppOnPrem) {
      return {
        content: renderPickApplication(submitFormAnswersButton),
        title: 'Welcome! What are you working on ?',
        isPrimaryButtonDisabled: false,
        primaryButtonText: null
      };
    }

    if (shouldDisplayPickApp && activeStepIndex === 0) {
      return {
        content: renderPickApplication(selectApplicationType),
        title: 'Welcome! What are you working on ?',
        isPrimaryButtonDisabled: false,
        primaryButtonText: null
      };
    }

    const questionsStep = role ? 3 : teamSize ? 2 : 1;
    const questionsTitle = shouldDisplayPickApp
      ? `Tell us more about yourself (${questionsStep} of 3)`
      : 'Hey, tell us more about yourself';

    return {
      content: renderTellUsMoreContent(),
      title: questionsTitle,
      handlePrimaryButtonClick: (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        submitFormAnswersButton(applicationType);
      },
      isPrimaryButtonDisabled: !modeling,
      primaryButtonText: 'Done'
    };
  };
  const tab = getTab();

  const hasStepsToComplete = IS_ON_PREMISE ? shouldDisplayPickAppOnPrem : true;
  const isModalOpen =
    isUserLoggedIn &&
    hasStepsToComplete &&
    !isPostSignupFormAlreadyShown &&
    !isSelectedAppLLM;

  const stepAmount = !IS_ON_PREMISE && shouldDisplayPickApp ? 2 : 1;

  return (
    <FullWidthBasicModal
      title={tab.title}
      open={isModalOpen}
      content={tab.content}
      primaryButtonText={tab.primaryButtonText}
      isPrimaryButtonDisabled={tab.isPrimaryButtonDisabled}
      onPrimaryButtonClick={tab.handlePrimaryButtonClick}
      stepActiveIndex={activeStepIndex}
      stepAmount={stepAmount}
      onBackButtonClick={() => {
        setActiveStepIndex(step => Math.max(step - 1, 0));
      }}
      withBackButton
      // TS compatibility
      onClose={undefined}
      variant={undefined}
      secondaryButtonText={undefined}
      onSecondaryButtonClick={undefined}
      isPrimaryButtonLoading={undefined}
      withoutFooter={undefined}
      className={styles.postSignUpModal}
      footer={undefined}
      customHeaderContent={undefined}
      headerTitle={undefined}
      titleMaskTest={undefined}
    />
  );
};

export default PostSignUpQuestions;
